<template>
    <div class="image-upload">
        <label v-if="showLabel" class="image-upload__label">
            <slot name="label" />
        </label>
        <img v-if="image" class="image-upload__image" :src="image" />
        <div class="image-upload__upload-wrapper">
            <hox-loading-layer v-if="isUploading" :is-full-screen="false" size="small" />
            <Upload
                v-if="Object.keys(uploadConfig).length"
                :show-upload-list="false"
                :action="uploadConfig.action"
                :data="uploadConfig.data"
                :multiple="false"
                :before-upload="beforeUpload"
                :on-success="onSuccess"
                :on-error="onError"
                type="drag"
            >
                <div class="image-upload__uploader">
                    <Icon size="38" type="ios-cloud-upload-outline" />
                    <p>Drag and drop (or click) to upload an image</p>
                </div>
            </Upload>
        </div>
        <p v-if="hasErrorUploading" class="image-upload__error-message">
            There was an error while uploading the image. Please try again in a few moments.
        </p>
    </div>
</template>

<script lang="ts">
import type { MastheadUploadConfig } from "@/types/types";
import type { PropType } from "vue";

type UploadConfig = {
    action: string;
    data: Record<string, string>;
    filePrefix: string;
    postData: string;
};

export default {
    props: {
        modelValue: { type: String as PropType<string | undefined>, required: true },
        config: { type: Object as PropType<MastheadUploadConfig>, required: true }
    },

    emits: ["imageUploaded", "imageUploadFailed"],

    data() {
        return {
            image: undefined as string | undefined,
            hasErrorUploading: false,
            isUploading: false,
            file: null as string | null,
            uploadConfig: {} as UploadConfig
        };
    },
    computed: {
        showLabel() {
            return this.$slots.label !== undefined;
        }
    },
    watch: {
        modelValue(v: string | undefined) {
            this.image = v;
        },
        config(c: MastheadUploadConfig) {
            this.processConfig(c);
        }
    },
    mounted() {
        if (this.config) {
            this.image = this.modelValue;
            this.processConfig(this.config);
        }
    },
    unmounted() {
        if (this.file) {
            URL.revokeObjectURL(this.file);
        }
    },
    methods: {
        processConfig(config: MastheadUploadConfig) {
            this.uploadConfig = { ...config, data: {} };
            this.uploadConfig.data = JSON.parse(this.uploadConfig.postData);
            this.uploadConfig.data.key = this.uploadConfig.filePrefix;
        },
        beforeUpload(file: File) {
            this.file = null;
            this.uploadConfig.data.key += `/${file.name}`;

            const isAcceptedFileType = ["image/jpeg", "image/png"].includes(file.type);
            if (!isAcceptedFileType) {
                this.$snackbar.image({ message: "The image must be either a PNG or JPEG" });
                return false;
            }

            const isLt2M = file.size / 1024 / 1024 < 2;
            if (!isLt2M) {
                this.$snackbar.image({ message: "Image size can not exceed 2MB" });
                return false;
            }

            this.file = URL.createObjectURL(file);
            this.isUploading = true;

            return true;
        },
        onSuccess() {
            this.isUploading = false;
            this.image = this.file as string;
            this.$emit("imageUploaded", this.uploadConfig.data.key);
        },
        onError() {
            this.isUploading = false;
            this.hasErrorUploading = true;
            this.$emit("imageUploadFailed");
        }
    }
};
</script>

<style lang="scss">
@use "@style/variables" as *;

.image-upload {
    display: inline-block;
    width: 100%;

    .ivu-upload-drag {
        color: $link-color;
        transition: background, border-color, color;
        transition-timing-function: $transition-function-base;
        transition-duration: $transition-time-base;

        &:hover {
            color: $link-hover-color;
            border-color: $link-hover-color;
        }
    }

    .ivu-upload-dragOver {
        background: rgba($link-color, 0.15);
        border: 1px dashed $link-hover-color;
        color: $link-hover-color;
    }

    &__error-message {
        color: $error-color;
        font-size: $font-size-small;
        margin: 0 0 $spacing;
    }

    &__image {
        max-width: 100%;
        max-height: 150px;
        display: block;
        border: 1px solid $cmp-light-border-color;
        padding: $spacing-smallest;
        margin: 0 0 $spacing-small;
    }

    &__label {
        color: $cmp-light-secondary-font-color;
        display: inline-block;
        font-size: $font-size-base;
        margin: 0 0 $spacing-small;
    }

    &__upload-wrapper {
        position: relative;
        margin: 0 0 $spacing-small;
    }

    &__uploader {
        font-size: $font-size-base;
        width: 100%;
        text-align: center;
        cursor: pointer;
        padding: $spacing-large $spacing;

        .ivu-icon {
            margin: 0 0 $spacing-small;
        }
    }
}
</style>
